import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import styled from 'styled-components';

const StyledTextInputContainer = styled.div`
  display: flex;
  flex-direction: column;

  .password-field-container {
    position: relative;
    display: block;
    width: 100%;
    height: auto;

    input {
      display: block;
      width: 100%;
    }
  } 

  .toggle-eye-icon {
    position: absolute;
    bottom: 3px;
    right: 10px;
    cursor: pointer;
    user-select: none;

    svg {
      width: 22px;
      height: auto;
    }
  }

  .type-text {
    filter: saturate(0%) brightness(70%) contrast(1000%);
  }
`;

const PasswordInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const {
    title,
    identifier,
    hint,
    hidden, 
    validate_msg: validateMsg,
  } = element;
  const { register } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));
  
  const {
    hasResourceUpdateHighlight,
    removeResourceUpdateHighlight,
  } = useResourceUpdateHighlight({fieldIdentifier: identifier});

  const [{ isActive, isElementValid, isRequired, isReadOnly, initialAnswer }] =
    useRules(element, formContext);
  const [fieldType, setFieldType] = useState(element.type);

  useEffect(() => {
    register(identifier, {
      validate: () => isElementValid || validateMsg || false,
      required: isRequired ? 'This field is required' : false,
    });
  }, [identifier, isElementValid, validateMsg, isRequired]);

  useEffect(() => {
    changeValue(initialAnswer);
  }, [initialAnswer]);

  const onChange = (value) => {
    changeValue(value, true);
  };

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  const commonProps = {
    type: 'password',
    name: identifier,
    id: identifier,
    // value: currentValue ?? '',
    defaultValue: currentValue ?? '',
    readOnly: isReadOnly,
    className: clsx(
      errorMessage !== undefined && 'invalid',
      hasResourceUpdateHighlight && 'updateHighlight',
      isReadOnly && 'read-only',
    ),
    onChange: (e) => onChange(e.target.value),
    onFocus: removeResourceUpdateHighlight,
  };

  const toggleFieldType = () => {
    if(fieldType === 'password') {
      setFieldType('text');
    } else {
      setFieldType('password');
    }
  };

  return (
    <div className="react-form-control">
      <StyledTextInputContainer>
        <label htmlFor={identifier}>{title}</label>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <div className="password-field-container">
          <input {...commonProps} type={fieldType} />
          <span onClick={toggleFieldType} className={`${clsx('toggle-eye-icon', fieldType === 'password' ? 'type-password' : 'type-text')}`}>
            <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.0007 12C15.0007 13.6569 13.6576 15 12.0007 15C10.3439 15 9.00073 13.6569 9.00073 12C9.00073 10.3431 10.3439 9 12.0007 9C13.6576 9 15.0007 10.3431 15.0007 12Z" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12.0012 5C7.52354 5 3.73326 7.94288 2.45898 12C3.73324 16.0571 7.52354 19 12.0012 19C16.4788 19 20.2691 16.0571 21.5434 12C20.2691 7.94291 16.4788 5 12.0012 5Z" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
        {hint && <p className="element-hint">{hint}</p>}
        {errorMessage !== undefined && (
          <ErrorMessage id={`${identifier}_errmsg`}>
            {errorMessage}
          </ErrorMessage>
        )}
      </StyledTextInputContainer>
    </div>
  );
};

PasswordInput.propTypes = {
  element: PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    validate_msg: PropTypes.string,
    initialAnswer: PropTypes.string,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(PasswordInput);
